import { useCallback, useState } from "react";
import api from "utils/api";
import type { FloorPlanRecord } from "components/SmartSurveyor/SmartSurveyorForRudolfFritz/hooks/types";

interface UseRenamePlanArgs {
  planRecords: FloorPlanRecord[];
  onPlanRenamed?: (recordIndex: number, newName: string) => void;
}

export const useRenamePlan = ({
  planRecords,
  onPlanRenamed,
}: UseRenamePlanArgs) => {
  const [renamingRecordIndex, setRenamingRecordIndex] = useState<number | null>(
    null,
  );
  const [isRenamingLoading, setIsRenamingLoading] = useState(false);

  const renamingRecord =
    renamingRecordIndex !== null
      ? (planRecords[renamingRecordIndex] ?? null)
      : null;

  const isRenamingOpen = renamingRecord !== null;

  const openRenamePlan = useCallback(
    (recordIndex: number) => {
      if (!planRecords[recordIndex]) return;
      setRenamingRecordIndex(recordIndex);
    },
    [planRecords],
  );

  const closeRenamePlan = useCallback(() => {
    setRenamingRecordIndex(null);
  }, []);

  const submitRenamePlan = useCallback(
    async (newName: string) => {
      if (renamingRecord === null || renamingRecordIndex === null) return;
      setIsRenamingLoading(true);
      try {
        await api.updateQuantitySurveyorFloorPlan(renamingRecord.id, {
          name: newName,
        });
        onPlanRenamed?.(renamingRecordIndex, newName);
        setRenamingRecordIndex(null);
      } finally {
        setIsRenamingLoading(false);
      }
    },
    [renamingRecord, renamingRecordIndex, onPlanRenamed],
  );

  return {
    isRenamingOpen,
    isRenamingLoading,
    renamingPlanLabel: renamingRecord?.name ?? "",
    openRenamePlan,
    closeRenamePlan,
    submitRenamePlan,
  };
};
